// In Next.js, this file would be called: app/providers.tsx
'use client';
// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { WsLogger } from '@/utils/wsLogger';
import { isServer, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WorkspaceDraftsWarning } from '~/app/owner-portal/workspace/workspace_drafts';
import { IS_DEVELOPMENT } from '~/modules/config';
import { featureFlagsStore } from '~/modules/stores/feature_flags_store';
import { logger, setCrossEnvLogger } from '~/modules/utilities/cross_env_utils';
import { emptyObject } from '~/modules/utilities/object_utils';
import { installSessionRenewal } from '~/services/userSessionService';
import './global.css';

const wsLogger = new WsLogger();
setCrossEnvLogger(wsLogger);

if (typeof window !== 'undefined') {
  const buildTimestamp = process.env.NEXT_PUBLIC_BUILD_TIMESTAMP || '';
  logger.log(
    `${process.env.NEXT_PUBLIC_APP_NAME} v${process.env.NEXT_PUBLIC_APP_VERSION}${
      buildTimestamp ? ` built on ${new Date(buildTimestamp).toLocaleString()}` : ''
    }${IS_DEVELOPMENT ? ' (Development)' : ''}`,
  );
  /* UNLEASH: Initialize FFs using server values, but then fetch the latest ones
  featureFlagsStore.getFeatureFlags({
    setValues: window.stores?.FEATURE_FLAGS,
  }); */
  featureFlagsStore.setState({
    featureFlags: window.stores?.FEATURE_FLAGS ?? emptyObject(),
  });
  // One renewal attempt behind every request in the tab, before any page sees a rejection.
  installSessionRenewal();
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  browserQueryClient ??= makeQueryClient();
  return browserQueryClient;
}

export function Providers({ children }: { children: React.ReactNode }) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      {/* The owner portal workspace's unsaved text outlives the portal's pages, so its warning lives here. */}
      <WorkspaceDraftsWarning />
      {children}
    </QueryClientProvider>
  );
}
